<template>
  <div class="forgot-password-fullscreen-wrapper">
    <div class="forgot-password-container">
      <div class="forgot-password-card-wrapper">
        <div class="card shadow-lg border-0">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div
              class="bg-gradient-warning shadow-warning border-radius-lg py-4 pe-1"
            >
              <div class="text-center mb-3">
                <div class="icon-wrapper">
                  <i :class="getHeaderIcon" class="text-white"></i>
                </div>
              </div>
              <h4 class="text-white font-weight-bolder text-center mb-0">
                {{ getHeaderTitle }}
              </h4>
              <p
                class="text-white text-sm text-center mt-2 mb-0 px-3 opacity-8"
              >
                {{ getHeaderSubtitle }}
              </p>
            </div>
          </div>

          <div class="card-body px-4 py-4">
            <!-- Error Message -->
            <div
              v-if="error"
              class="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              <div class="alert-content">
                <i class="fas fa-exclamation-circle"></i>
                <span>{{ error }}</span>
              </div>
              <button
                type="button"
                class="btn-close"
                @click="error = ''"
                aria-label="Close"
              ></button>
            </div>

            <!-- Success Message -->
            <div
              v-if="successMessage && currentStep !== 3"
              class="alert alert-success alert-dismissible fade show"
              role="alert"
            >
              <div class="alert-content">
                <i class="fas fa-check-circle"></i>
                <span>{{ successMessage }}</span>
              </div>
              <button
                type="button"
                class="btn-close"
                @click="successMessage = ''"
                aria-label="Close"
              ></button>
            </div>

            <!-- Progress Steps Indicator -->
            <div v-if="currentStep < 4" class="progress-steps mb-4">
              <div
                class="step"
                :class="{
                  active: currentStep >= 1,
                  completed: currentStep > 1,
                }"
              >
                <div class="step-circle">
                  <i v-if="currentStep > 1" class="fas fa-check"></i>
                  <span v-else>1</span>
                </div>
                <div class="step-label">Email</div>
              </div>
              <div
                class="step-line"
                :class="{ active: currentStep >= 2 }"
              ></div>
              <div
                class="step"
                :class="{
                  active: currentStep >= 2,
                  completed: currentStep > 2,
                }"
              >
                <div class="step-circle">
                  <i v-if="currentStep > 2" class="fas fa-check"></i>
                  <span v-else>2</span>
                </div>
                <div class="step-label">Verify</div>
              </div>
              <div
                class="step-line"
                :class="{ active: currentStep >= 3 }"
              ></div>
              <div class="step" :class="{ active: currentStep >= 3 }">
                <div class="step-circle">3</div>
                <div class="step-label">Reset</div>
              </div>
            </div>

            <!-- STEP 1: Email Input -->
            <form
              v-if="currentStep === 1"
              role="form"
              class="text-start mt-3"
              @submit.prevent="handleSendOtp"
              autocomplete="off"
            >
              <div style="display: none; height: 0; overflow: hidden">
                <input type="text" name="dummy_username" autocomplete="off" />
              </div>

              <div class="input-section">
                <material-input
                  id="reset_email"
                  type="email"
                  label="Email Address"
                  name="reset_email"
                  :value="email"
                  @input="handleEmailInput"
                  autocomplete="off"
                  :disabled="isLoading"
                  size="lg"
                />
                <small v-if="emailError" class="text-danger d-block mt-2">
                  <i class="fas fa-exclamation-circle me-1"></i>
                  {{ emailError }}
                </small>
              </div>

              <div class="text-center mt-4">
                <material-button
                  class="mb-3"
                  variant="gradient"
                  color="warning"
                  fullWidth
                  type="submit"
                  :disabled="isLoading"
                >
                  <span v-if="isLoading">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    Sending OTP...
                  </span>
                  <span v-else>
                    <i class="fas fa-paper-plane me-2"></i>
                    Send Verification Code
                  </span>
                </material-button>
              </div>

              <div class="text-center mt-4">
                <router-link :to="{ name: 'SignIn' }" class="back-link">
                  <i class="fas fa-arrow-left me-1"></i>
                  Back to Sign In
                </router-link>
              </div>
            </form>

            <!-- STEP 2: OTP Input -->
            <form
              v-else-if="currentStep === 2"
              role="form"
              class="text-start mt-2"
              @submit.prevent="handleVerifyOtp"
              autocomplete="off"
            >
              <div class="input-section">
                <div class="otp-input-container">
                  <input
                    v-for="(digit, index) in 6"
                    :key="index"
                    type="text"
                    class="otp-input"
                    :ref="`otpInput${index}`"
                    :value="otpDigits[index]"
                    @input="handleOtpDigitInput(index, $event)"
                    @keydown="handleOtpKeydown(index, $event)"
                    @paste="handleOtpPaste"
                    maxlength="1"
                    inputmode="numeric"
                    :disabled="isLoading"
                  />
                </div>
                <small
                  v-if="otpError"
                  class="text-danger d-block mt-2 text-center"
                >
                  <i class="fas fa-exclamation-circle me-1"></i>
                  {{ otpError }}
                </small>
              </div>

              <div class="text-center mt-2">
                <material-button
                  class="mb-3"
                  variant="gradient"
                  color="warning"
                  fullWidth
                  type="submit"
                  :disabled="isLoading"
                >
                  <span v-if="isLoading">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    Verifying...
                  </span>
                  <span v-else>
                    <i class="fas fa-shield-alt me-2"></i>
                    Verify Code
                  </span>
                </material-button>
              </div>

              <div class="resend-section text-center">
                <!-- <small class="text-secondary"> Didn't receive the code? </small> -->
                <button
                  type="button"
                  @click="handleResendOtp"
                  :disabled="isLoading || resendCountdown > 0"
                  class="resend-button"
                >
                  <span v-if="resendCountdown > 0" class="text-muted">
                    Resend in {{ resendCountdown }}s
                  </span>
                  <span v-else class="text-warning font-weight-bold">
                    <i class="fas fa-redo me-1"></i>
                    Resend Code
                  </span>
                </button>
              </div>

              <div class="text-center mt-2">
                <button type="button" @click="goBackToEmail" class="back-link">
                  <i class="fas fa-arrow-left me-1"></i>
                  Change Email
                </button>
              </div>
            </form>

            <!-- STEP 3: Password Reset -->
            <form
              v-else-if="currentStep === 3"
              role="form"
              class="text-start mt-3"
              @submit.prevent="handleChangePassword"
              autocomplete="off"
            >
              <div class="input-section">
                <div class="mb-4">
                  <material-input
                    id="new_password"
                    :type="showPassword ? 'text' : 'password'"
                    label="New Password"
                    name="new_password"
                    :value="newPassword"
                    @input="handlePasswordInput"
                    :disabled="isLoading"
                    size="lg"
                    autocomplete="off"
                  />
                  <small v-if="passwordError" class="text-danger d-block mt-2">
                    <i class="fas fa-exclamation-circle me-1"></i>
                    {{ passwordError }}
                  </small>
                </div>

                <div class="mb-4">
                  <material-input
                    id="confirm_password"
                    :type="showPassword ? 'text' : 'password'"
                    label="Confirm Password"
                    name="confirm_password"
                    :value="confirmPassword"
                    @input="handleConfirmPasswordInput"
                    :disabled="isLoading"
                    size="lg"
                    autocomplete="off"
                  />
                  <small
                    v-if="confirmPasswordError"
                    class="text-danger d-block mt-2"
                  >
                    <i class="fas fa-exclamation-circle me-1"></i>
                    {{ confirmPasswordError }}
                  </small>
                </div>

                <div class="form-check mb-4">
                  <input
                    id="showPassword"
                    type="checkbox"
                    class="form-check-input"
                    v-model="showPassword"
                  />
                  <label class="form-check-label text-sm" for="showPassword">
                    Show passwords
                  </label>
                </div>
              </div>

              <div class="text-center">
                <material-button
                  class="mb-2"
                  variant="gradient"
                  color="warning"
                  fullWidth
                  type="submit"
                  :disabled="isLoading"
                >
                  <span v-if="isLoading">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    Updating Password...
                  </span>
                  <span v-else>
                    <i class="fas fa-key me-2"></i>
                    Update Password
                  </span>
                </material-button>
              </div>
            </form>

            <!-- STEP 4: Success -->
            <div
              v-else-if="currentStep === 4"
              class="success-section text-center mt-4"
            >
              <div class="success-icon-wrapper mb-4">
                <i class="fas fa-check-circle text-success"></i>
              </div>
              <h5 class="success-title mb-3">Password Reset Complete!</h5>
              <p class="success-message mb-4">
                Your password has been successfully updated. You can now sign in
                with your new credentials.
              </p>
              <router-link
                :to="{ name: 'SignIn' }"
                class="btn btn-lg btn-warning px-5"
              >
                <i class="fas fa-sign-in-alt me-2"></i>
                Sign In Now
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialInput from "@/components/MaterialInput.vue";
import MaterialButton from "@/components/MaterialButton.vue";
import axios from "axios";

export default {
  name: "forgot-password",
  components: {
    MaterialInput,
    MaterialButton,
  },
  data() {
    return {
      currentStep: 1, // 1: Email, 2: OTP, 3: Password, 4: Success
      email: "",
      otpDigits: ["", "", "", "", "", ""],
      verifiedOtp: "", // Store the verified OTP for password reset
      newPassword: "",
      confirmPassword: "",
      showPassword: false,

      isLoading: false,
      emailError: "",
      otpError: "",
      passwordError: "",
      confirmPasswordError: "",
      error: "",
      successMessage: "",

      resendCountdown: 0,
      resendInterval: null,
    };
  },
  computed: {
    getHeaderTitle() {
      const titles = {
        1: "Reset Password",
        2: "",
        3: "Create New Password",
        4: "All Set!",
      };
      return titles[this.currentStep];
    },
    getHeaderSubtitle() {
      const subtitles = {
        1: "We'll send a verification code to your email",
        2: "Enter the code we sent to your email",
        3: "Choose a strong password for your account",
        4: "Your password has been successfully updated",
      };
      return subtitles[this.currentStep];
    },
    getHeaderIcon() {
      const icons = {
        1: "fas fa-envelope fa-3x",
        2: "fas fa-shield-alt fa-3x",
        3: "fas fa-lock fa-3x",
        4: "fas fa-check-circle fa-3x",
      };
      return icons[this.currentStep];
    },
  },
  mounted() {
    document.body.classList.remove("g-sidenav-show", "g-sidenav-pinned");
    document.body.style.overflow = "hidden";
  },
  beforeUnmount() {
    document.body.style.overflow = "";
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
  },
  methods: {
    // STEP 1: Email Input
    handleEmailInput(event) {
      this.email = event.target ? event.target.value : event;
      this.validateEmail();
    },

    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.email && !emailRegex.test(this.email)) {
        this.emailError = "Please enter a valid email address";
      } else {
        this.emailError = "";
      }
    },

    async handleSendOtp() {
      this.error = "";
      this.emailError = "";

      if (!this.email) {
        this.emailError = "Please enter your email address";
        return;
      }

      this.validateEmail();
      if (this.emailError) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await axios.post("/forgot-password/otp/", {
          email: this.email.trim(),
        });

        if (response.status === 200 || response.status === 201) {
          this.successMessage = response.data?.message || "";
          this.currentStep = 2;
          this.resendCountdown = 0;
          setTimeout(() => {
            this.successMessage = "";
          }, 30000);
        }
      } catch (error) {
        console.error("Send OTP error:", error);
        this.error =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to send OTP. Please try again.";

        if (error.response?.status === 404) {
          this.error = "No account found with this email address.";
        }
      } finally {
        this.isLoading = false;
      }
    },

    // STEP 2: OTP Input
    handleOtpDigitInput(index, event) {
      let value = event.target.value;
      value = value.replace(/[^0-9]/g, "");
      this.otpDigits[index] = value;

      if (value && index < 5) {
        this.$nextTick(() => {
          const nextInput = this.$refs[`otpInput${index + 1}`]?.[0];
          if (nextInput) {
            nextInput.focus();
          }
        });
      }

      this.validateOtp();
    },

    handleOtpKeydown(index, event) {
      if (event.key === "Backspace" && !this.otpDigits[index] && index > 0) {
        const prevInput = this.$refs[`otpInput${index - 1}`]?.[0];
        if (prevInput) {
          prevInput.focus();
        }
      }
      if (event.key === "ArrowLeft" && index > 0) {
        const prevInput = this.$refs[`otpInput${index - 1}`]?.[0];
        if (prevInput) {
          prevInput.focus();
        }
      }
      if (event.key === "ArrowRight" && index < 5) {
        const nextInput = this.$refs[`otpInput${index + 1}`]?.[0];
        if (nextInput) {
          nextInput.focus();
        }
      }
    },

    handleOtpPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData("text");
      const digits = pastedData.replace(/[^0-9]/g, "").split("");

      for (let i = 0; i < 6 && i < digits.length; i++) {
        this.otpDigits[i] = digits[i];
      }

      const firstEmptyIndex = this.otpDigits.findIndex((d) => !d);
      const focusIndex = firstEmptyIndex === -1 ? 5 : firstEmptyIndex;
      this.$nextTick(() => {
        const input = this.$refs[`otpInput${focusIndex}`]?.[0];
        if (input) {
          input.focus();
        }
      });

      this.validateOtp();
    },

    validateOtp() {
      const otp = this.otpDigits.join("");
      if (otp.length < 6) {
        this.otpError = "Please enter all 6 digits";
      } else {
        this.otpError = "";
      }
    },

    async handleVerifyOtp() {
      this.error = "";
      this.otpError = "";

      const otp = this.otpDigits.join("");
      if (!otp || otp.length < 6) {
        this.otpError = "Please enter all 6 digits";
        return;
      }

      this.isLoading = true;

      try {
        const response = await axios.post("/forgot-password/verify-otp/", {
          email: this.email.trim(),
          otp: otp,
        });

        if (response.status === 200 || response.status === 201) {
          this.successMessage =
            response.data?.message || "OTP verified successfully.";
          this.verifiedOtp = otp;
          this.currentStep = 3;
          this.otpDigits = ["", "", "", "", "", ""];
          setTimeout(() => {
            this.successMessage = "";
          }, 30000);
        }
      } catch (error) {
        console.error("Verify OTP error:", error);
        this.error =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Invalid OTP. Please try again.";

        if (error.response?.status === 400) {
          this.error =
            "OTP is invalid or has expired. Please request a new one.";
        }
      } finally {
        this.isLoading = false;
      }
    },

    async handleResendOtp() {
      this.error = "";
      this.isLoading = true;

      try {
        const response = await axios.post("/token/resend-otp/", {
          email: this.email.trim(),
        });

        if (response.status === 200 || response.status === 201) {
          this.successMessage =
            response.data?.message ||
            "OTP resent successfully. Check your email.";
          this.startResendCountdown();
          setTimeout(() => {
            this.successMessage = "";
          }, 30000);
        }
      } catch (error) {
        console.error("Resend OTP error:", error);
        this.error =
          error.response?.data?.message ||
          "Failed to resend OTP. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },

    startResendCountdown() {
      this.resendCountdown = 60;
      if (this.resendInterval) {
        clearInterval(this.resendInterval);
      }
      this.resendInterval = setInterval(() => {
        this.resendCountdown--;
        if (this.resendCountdown <= 0) {
          clearInterval(this.resendInterval);
          this.resendInterval = null;
        }
      }, 1000);
    },

    goBackToEmail() {
      this.currentStep = 1;
      this.otpDigits = ["", "", "", "", "", ""];
      this.otpError = "";
      this.error = "";
      this.successMessage = "";
    },

    // STEP 3: Password Reset
    handlePasswordInput(event) {
      this.newPassword = event.target ? event.target.value : event;
      this.validatePassword();
    },

    handleConfirmPasswordInput(event) {
      this.confirmPassword = event.target ? event.target.value : event;
      this.validateConfirmPassword();
    },

    validatePassword() {
      if (this.newPassword.length < 8) {
        this.passwordError = "Password must be at least 8 characters long";
      } else {
        this.passwordError = "";
      }
    },

    validateConfirmPassword() {
      if (this.newPassword !== this.confirmPassword) {
        this.confirmPasswordError = "Passwords do not match";
      } else {
        this.confirmPasswordError = "";
      }
    },

    async handleChangePassword() {
      this.error = "";
      this.passwordError = "";
      this.confirmPasswordError = "";

      if (!this.newPassword) {
        this.passwordError = "Please enter a new password";
        return;
      }

      if (!this.confirmPassword) {
        this.confirmPasswordError = "Please confirm your password";
        return;
      }

      this.validatePassword();
      this.validateConfirmPassword();

      if (this.passwordError || this.confirmPasswordError) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await axios.post("/forgot-password/reset/", {
          email: this.email.trim(),
          new_password: this.confirmPassword,
          otp: this.verifiedOtp,
        });

        if (response.status === 200 || response.status === 201) {
          this.successMessage =
            response.data?.message || "Password changed successfully!";
          this.currentStep = 4;
          this.newPassword = "";
          this.confirmPassword = "";
          this.verifiedOtp = "";
          setTimeout(() => {
            this.successMessage = "";
          }, 30000);
        }
      } catch (error) {
        console.error("Change password error:", error);
        this.error =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to change password. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.forgot-password-fullscreen-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    #f8f9fa 0%,
    #e9ecef 25%,
    #dee2e6 50%,
    #e9ecef 75%,
    #f8f9fa 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1.5rem;
  overflow-y: auto;
}

.forgot-password-container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: auto;
}

.forgot-password-card-wrapper {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
}

.card {
  border-radius: 1.25rem;
  border: none;
  box-shadow: 0 20px 60px 0 rgba(0, 0, 0, 0.08);
  overflow: visible;
  transition: all 0.3s ease;
  background: #ffffff;
}

.card:hover {
  box-shadow: 0 25px 70px 0 rgba(0, 0, 0, 0.12);
}

.card-header > div {
  min-height: auto !important;
  padding: 1.5rem 1.5rem !important;
  border-radius: 1rem !important;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  backdrop-filter: blur(10px);
}

.icon-wrapper i {
  font-size: 2rem;
}

.card-header h4 {
  font-size: 1.5rem;
  margin-top: 0.75rem !important;
  font-weight: 700;
}

.card-header p {
  font-size: 0.8125rem;
  margin-top: 0.25rem !important;
}

.card-body {
  padding: 1.75rem 2rem !important;
}

/* Progress Steps */
.progress-steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem;
  background: #f8f9fa;
  border-radius: 0.75rem;
  margin-bottom: 1.5rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 auto;
}

.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.8125rem;
  transition: all 0.3s ease;
  border: 2px solid #e9ecef;
}

.step.active .step-circle {
  background: linear-gradient(310deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  border-color: #fbbf24;
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4);
}

.step.completed .step-circle {
  background: #28a745;
  color: white;
  border-color: #28a745;
}

.step-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.step.active .step-label {
  color: #fbbf24;
}

.step.completed .step-label {
  color: #28a745;
}

.step-line {
  flex: 1;
  height: 3px;
  background: #e9ecef;
  margin: 0 0.5rem;
  margin-bottom: 1.5rem;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.step-line.active {
  background: linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%);
}

/* Input Section */
.input-section {
  margin-bottom: 1rem;
}

/* Alerts */
.alert {
  border-radius: 0.75rem;
  border: none !important;
  font-size: 0.875rem;
  padding: 0.875rem 1rem !important;
  margin-bottom: 1rem !important;
}

.alert-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.alert-content i {
  font-size: 1.25rem;
}

.alert.alert-success {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%) !important;
  color: #155724 !important;
  border-left: 4px solid #28a745 !important;
}

.alert.alert-danger {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%) !important;
  color: #721c24 !important;
  border-left: 4px solid #dc3545 !important;
}

/* OTP Input */
.otp-input-container {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem 0;
}

.otp-input {
  width: 50px;
  height: 50px;
  font-size: 1.5rem;
  text-align: center;
  border: 2px solid #dee2e6;
  border-radius: 0.75rem;
  font-weight: 700;
  transition: all 0.2s ease;
  background: #ffffff;
  color: #344767;
}

.otp-input:focus {
  outline: none;
  border-color: #fbbf24;
  box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.15);
  transform: scale(1.05);
}

.otp-input:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
  opacity: 0.6;
}

.otp-input:not(:disabled):hover {
  border-color: #fbbf24;
}

/* Resend Section */
.resend-section {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
}

.resend-button {
  background: none;
  border: none;
  padding: 0.5rem 0;
  cursor: pointer;
  display: block;
  margin: 0.5rem auto 0;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.resend-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.resend-button:not(:disabled):hover {
  transform: translateY(-1px);
}

/* Success Section */
.success-section {
  padding: 1.5rem 1rem;
}

.success-icon-wrapper {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  animation: successPulse 0.6s ease-out;
}

.success-icon-wrapper i {
  font-size: 2.75rem;
}

@keyframes successPulse {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #28a745;
}

.success-message {
  font-size: 0.9375rem;
  color: #6c757d;
  line-height: 1.6;
}

/* Links and Buttons */
.back-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: none;
  font-size: 0.875rem;
  color: #fbbf24;
  font-weight: 600;
  transition: all 0.2s ease;
  display: inline-block;
}

.back-link:hover {
  color: #f59e0b;
  transform: translateX(-3px);
}

.bg-gradient-warning {
  background: linear-gradient(310deg, #fbbf24 0%, #f59e0b 100%);
}

.shadow-warning {
  box-shadow:
    0 4px 20px 0 rgba(251, 191, 36, 0.18),
    0 7px 10px -5px rgba(245, 158, 11, 0.4);
}

.material-button {
  padding: 0.75rem 1.5rem !important;
  font-size: 0.9375rem !important;
  font-weight: 600 !important;
  border-radius: 0.75rem !important;
  min-height: 48px;
  transition: all 0.3s ease;
}

.material-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(251, 191, 36, 0.3);
}

.form-check-input {
  width: 1.25em;
  height: 1.25em;
  margin-top: 0.125em;
  vertical-align: top;
  border: 2px solid #dee2e6;
  border-radius: 0.25em;
  appearance: none;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-check-input:checked {
  background-color: #fbbf24;
  border-color: #fbbf24;
}

.form-check-input:focus {
  box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.15);
}

.form-check-label {
  cursor: pointer;
  user-select: none;
}

/* Responsive */
@media (max-width: 576px) {
  .forgot-password-fullscreen-wrapper {
    padding: 1rem;
  }

  .card-body {
    padding: 1.5rem 1.25rem !important;
  }

  .card-header h4 {
    font-size: 1.375rem;
  }

  .card-header p {
    font-size: 0.75rem;
  }

  .otp-input {
    width: 45px;
    height: 45px;
    font-size: 1.375rem;
  }

  .otp-input-container {
    gap: 0.4rem;
  }

  .progress-steps {
    padding: 0.875rem 0.5rem;
  }

  .step-circle {
    width: 32px;
    height: 32px;
    font-size: 0.75rem;
  }

  .step-label {
    font-size: 0.6875rem;
  }

  .icon-wrapper {
    width: 50px;
    height: 50px;
  }

  .icon-wrapper i {
    font-size: 1.75rem;
  }
}

@media (max-width: 400px) {
  .otp-input {
    width: 40px;
    height: 40px;
    font-size: 1.125rem;
  }

  .step-circle {
    width: 30px;
    height: 30px;
    font-size: 0.6875rem;
  }

  .otp-input-container {
    gap: 0.3rem;
  }
}
</style>

<style>
.forgot-password-fullscreen-wrapper ~ .sidenav,
.forgot-password-fullscreen-wrapper ~ .navbar,
.forgot-password-fullscreen-wrapper ~ footer[class*="position-absolute"],
.forgot-password-fullscreen-wrapper ~ footer[class*="fixed-bottom"] {
  display: none !important;
  opacity: 0 !important;
  visibility: hidden !important;
}

.fullscreen-forgot-password {
  margin-left: 0 !important;
  width: 100% !important;
}
</style>
